import React, { FC, memo, useMemo, useState, useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom'
import styled from 'styled-components';
import { Colors, FInter, FlexBox, useBreakpoint, useIsMobile } from '@brg/ui';
import { CreateLinkTo, LinkTo } from '../../constants/linkTo';
import ProjectCardLayoutNew from '../projectCard/projectCardLayoutNew';
import { ProjectEvents } from "../../helpers/projectEvents";
import { ProjectTooltipProps } from '../tooltips/ProjectTooltip';
import { getProjectImages } from "../../pages/project/sections/ps-image";
import { useTranslation } from "react-i18next";
import { AppTester } from 'supporting';
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam';
import {ModerationType} from "../../pages/teams/const/ModerationTypes";
import {ProjectCardTitle} from "../projectCard/styles";
import { LMSCourseDurationTypes } from 'shared/pages/teams/const/LMSCourseTypes';
import { isPersonalOrDefaultChannel, TeamStatusActiveArchived } from 'shared/helpers/teamsStatus';
import { useProfile } from '@brg/auth';
import { TeamEditTooltip } from 'shared/pages/exploreHseContextsNew/components/TeamEditTooltip';
import { ProjectCardTools } from '../projectCard/ProjectCardTools';
import { LayoutConst } from 'core';
import { convertJsonToString } from 'shared/pages/exploreHseContextsNew/helpers/convertJsonToString';
import { Lang, Team } from '@brg/api';
import { getChannelName } from 'shared/pages/exploreHseContextsNew/helpers/getChannelName';
import { DesignerTab } from 'shared/constants/designerTab';

const Author = styled(FInter.SmallText)`
  font-size: 11px;
  line-height: 13px;
    
  color: ${Colors.geoWhite47};
  @media (max-width: 940px) {
    div {
      font-weight: 400;
    }
  }
`

export const StatusText = styled(FlexBox)<{hasCover?: boolean}>`
  position: absolute;
  top: 16px;
  left: 16px;
  margin-right: 100%;

  color: ${({ hasCover }) => hasCover ? '#fff' : '#909090'};
  text-shadow: 0 4px 4px rgba(0, 0, 0, 0.12);
  font-family: 'Inter';
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 13px; /* 118.182% */
  white-space: nowrap;
`;

export interface ChannelCardProps extends Partial<ComputedTeam>, ProjectEvents {
  isLoading?: boolean;
  isEditable?: boolean;
  isSelectable?: boolean;
  isDeletable?: boolean;
  isRejected?: boolean;
  isRemovableFromBook?: boolean;
  tooltip?: ProjectTooltipProps;
  width?: string;
  height?: string;
  entityType?: string;
  onShouldReload?: (sender: any) => void;
  onTeamClick?: (team: ComputedTeam, e?: any, openInMedia?: boolean) => void;
  onSelect?: (projectId: string) => void;
  onApprove?: (projectId: string) => void;
  onReject?: (projectId: string) => void;

  team?: ComputedTeam;
 
  isModal?: boolean;
  dataKey?: any;
  linkPrefix?: string;
  linkTo?: any;
}

const ChannelCard: FC<ChannelCardProps> = (props) => {
    const {
      isLoading,
      isSelectable,
      isDeletable,
      isRemovableFromBook,
      isEditable,
      isRejected,
      tooltip,
      width,
      height,
      onApprove,
      onReject,
      onSelect,
      onShouldReload,
      onTeamClick,
      onRemoveFromBook,
      team,
      contextName,
      linkPrefix,
      linkTo,
      entityType,
      ...handlers
    } = props;

    const { i18n } = useTranslation();
    const isOnlines = AppTester.isOnlines();
    const isMedia = AppTester.isMediiia();
    const profile = useProfile();
    const isFeed = useRouteMatch('/feed');
    const [isBookmarked, setIsBookmarked] = useState<boolean>()
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const isTimeLine = !!useRouteMatch('/timeline');
    const isTab = useBreakpoint(LayoutConst.screen.l);

    const [isTooltipOpen, setTooltipOpen] = useState<boolean>(false);

    const handleToolTipToggleMove = (val: boolean) => {
      setTooltipOpen && setTooltipOpen(val);
    }

    const titleImages = getProjectImages(team, i18n.language);

    useEffect(() => {
      if (!team) return;
      //@ts-ignore
      setIsBookmarked(team.isBookmark);
    }, [team]);
  
    const handleTeamClick = (projectId?: string, e?: any) => {
      const openInMedia = [/*ModerationType.Channel,*/ ModerationType.ChannelGroupOrCourse, ModerationType.Outer]?.includes(team?.moderationType ?? 0)
      team && onTeamClick && onTeamClick(team, e, openInMedia);
    }

    const lmsCardType = useMemo(() => {
      if (isPersonalOrDefaultChannel(team, profile) && entityType === DesignerTab.educationSubscribes) {
        return 'fixed-ratio-small'
      }
      return undefined;
    }, [team, entityType]);

    const horizontalCover = useMemo(() => {
      //@ts-ignore
      return (team?.lmsCourseTerm === LMSCourseDurationTypes.short) /*|| (isUnitCourse && team?.lmsCourseTerm === null)*/
    } , [team])
    // @ts-ignore
    const teamOwner = useMemo(() => team?.owners?.length > 0 ? `${team?.owners[0]?.name} ${team?.owners[0]?.surName}` : '', [team])
    
    const isArchiveCourseChannel = useMemo(() => {
      return team?.moderationType === ModerationType.ChannelGroupOrCourse && TeamStatusActiveArchived(team)
    } , [team])

    const backgroundStyles = useMemo(() => {
      if (isArchiveCourseChannel) {
        return {
          background: 'linear-gradient( 180deg,rgba(0, 0, 0, 0),  rgba(24, 26, 28, 0), rgba(24, 26, 28, 0.85) )',
          backdropFilter: 'blur(2.5px)',
          marginTop: 0
        }
      }
      return undefined
    } , [isArchiveCourseChannel])


    const badge = useMemo(() => {
      if (isArchiveCourseChannel) {
        return <StatusText hasCover={!!titleImages?.length}>{t('subscription.in_archive').toLowerCase()}</StatusText>
      }
      return undefined
    } , [isArchiveCourseChannel, titleImages])

    const title = useMemo(() => {
      if (isTimeLine) return null;

      else if (team?.moderationType === ModerationType.ChannelGroupOrCourse) {
        return <ProjectCardTitle {...(isArchiveCourseChannel ? { color: '#909090'} : {})}>{getChannelName(team as unknown as Team, i18n.language)}</ProjectCardTitle>
      }
      else if (team?.coverTitle?.length) {
        return <ProjectCardTitle >{team?.coverTitle}</ProjectCardTitle>
      } 
      else if (team?.displayName?.length) {
        return <ProjectCardTitle {...(isArchiveCourseChannel ? { color: '#909090'} : {})}>{team?.displayName}</ProjectCardTitle>
      }
      else if (team?.name?.length) {
        return <ProjectCardTitle {...(isArchiveCourseChannel ? { color: '#909090'} : {})}>{team?.name}</ProjectCardTitle>
      } else return null;
    }, [team, isTimeLine, isArchiveCourseChannel])

    const subscribersCount = useMemo(() => team?.subscribersCount , [team])

    const likes = useMemo(() => {
      const color = isMedia && isMobile ? Colors.galBlack100 : Colors.geoWhite100
      if (!subscribersCount || subscribersCount === 0) return null;
      return <Author style={{ color }}>{t('team_view.subscribers', { count: subscribersCount })}</Author>
    } , [team, subscribersCount])

      return (
      <ProjectCardLayoutNew
        title={title}
        coverTitle={!!team?.name ? <ProjectCardTitle>{team?.name}</ProjectCardTitle> : undefined}
        identifier={team?.teamId}
        onClick={handleTeamClick}
        titleImages={titleImages}
        isLoading={isLoading}
        width={width}
        team={team}
        height={height}
        isMoreVisible={true}
        isRejected={isRejected}
        metaAlwaysVisible={isTab && isEditable}
        tooltipOpen={isTooltipOpen}
        toggleTooltip={handleToolTipToggleMove}
        needLmsCover={horizontalCover}
        badge={badge}
        isMobileMoreVisible={isArchiveCourseChannel || isEditable}
        backgroundStyles={backgroundStyles}
        linkTo={linkTo}
        more={isEditable ? (
          <ProjectCardTools 
            isEditable
            tooltipOpen={isTooltipOpen}
            toggleTooltip={handleToolTipToggleMove}
            onEdit={() => {
              const isOuterTeam = team?.moderationType === ModerationType.Outer
              window.open(window.location.origin + `/${isOuterTeam ? 'ext-' : ''}team-edit/` + team?.teamId, '_SELF')
            }}
          />
        ) : null}
        
        // @ts-ignore
        projectType={lmsCardType}
        lmsCardType={lmsCardType}
        withoutGoToProject={!linkTo}
        author={
          (
            <Author>
              {/*@ts-ignore*/}
              <span style={{ color: isMedia && isMobile ? '#151514' :'#fefefe'}}>{teamOwner}</span>
            </Author>
          )
        }
        likes={likes}
        isTeam
      />
    );
  }
;

export default memo(ChannelCard);
import React, { FC, memo, useMemo, useState, useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom'
import styled from 'styled-components';
import { Colors, FInter, FlexBox, useBreakpoint, useIsMobile } from '@brg/ui';
import { CreateLinkTo, LinkTo } from '../../constants/linkTo';
import ProjectCardLayoutNew from '../projectCard/projectCardLayoutNew';
import { ProjectEvents } from "../../helpers/projectEvents";
import { ProjectTooltipProps } from '../tooltips/ProjectTooltip';
import { getProjectImages } from "../../pages/project/sections/ps-image";
import { useTranslation } from "react-i18next";
import { AppTester } from 'supporting';
import { ComputedTeam } from 'shared/helpers/types/computed/ComputedTeam';
import {ModerationType} from "../../pages/teams/const/ModerationTypes";
import {ProjectCardTitle} from "../projectCard/styles";
import { LMSCourseDurationTypes } from 'shared/pages/teams/const/LMSCourseTypes';
import { isPersonalOrDefaultChannel, TeamStatusActiveArchived } from 'shared/helpers/teamsStatus';
import { useProfile } from '@brg/auth';
import { TeamEditTooltip } from 'shared/pages/exploreHseContextsNew/components/TeamEditTooltip';
import { ProjectCardTools } from '../projectCard/ProjectCardTools';
import { LayoutConst } from 'core';
import { convertJsonToString } from 'shared/pages/exploreHseContextsNew/helpers/convertJsonToString';
import { Lang, Team } from '@brg/api';
import { getChannelName } from 'shared/pages/exploreHseContextsNew/helpers/getChannelName';
import { DesignerTab } from 'shared/constants/designerTab';

const Author = styled(FInter.SmallText)`
  font-size: 11px;
  line-height: 13px;
    
  color: ${Colors.geoWhite47};
  @media (max-width: 940px) {
    div {
      font-weight: 400;
    }
  }
`

export const StatusText = styled(FlexBox)<{hasCover?: boolean}>`
  position: absolute;
  top: 16px;
  left: 16px;
  margin-right: 100%;

  color: ${({ hasCover }) => hasCover ? '#fff' : '#909090'};
  text-shadow: 0 4px 4px rgba(0, 0, 0, 0.12);
  font-family: 'Inter';
  font-size: 11px;
  font-style: normal;
  font-weight: 600;
  line-height: 13px; /* 118.182% */
  white-space: nowrap;
`;

export interface ChannelCardProps extends Partial<ComputedTeam>, ProjectEvents {
  isLoading?: boolean;
  isEditable?: boolean;
  isSelectable?: boolean;
  isDeletable?: boolean;
  isRejected?: boolean;
  isRemovableFromBook?: boolean;
  tooltip?: ProjectTooltipProps;
  width?: string;
  height?: string;
  entityType?: string;
  onShouldReload?: (sender: any) => void;
  onTeamClick?: (team: ComputedTeam, e?: any, openInMedia?: boolean) => void;
  onSelect?: (projectId: string) => void;
  onApprove?: (projectId: string) => void;
  onReject?: (projectId: string) => void;

  team?: ComputedTeam;
 
  isModal?: boolean;
  dataKey?: any;
  linkPrefix?: string;
  linkTo?: any;
}

const ChannelCard: FC<ChannelCardProps> = (props) => {
    const {
      isLoading,
      isSelectable,
      isDeletable,
      isRemovableFromBook,
      isEditable,
      isRejected,
      tooltip,
      width,
      height,
      onApprove,
      onReject,
      onSelect,
      onShouldReload,
      onTeamClick,
      onRemoveFromBook,
      team,
      contextName,
      linkPrefix,
      linkTo,
      entityType,
      ...handlers
    } = props;

    const { i18n } = useTranslation();
    const isOnlines = AppTester.isOnlines();
    const isMedia = AppTester.isMediiia();
    const profile = useProfile();
    const isFeed = useRouteMatch('/feed');
    const [isBookmarked, setIsBookmarked] = useState<boolean>()
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const isTimeLine = !!useRouteMatch('/timeline');
    const isTab = useBreakpoint(LayoutConst.screen.l);

    const [isTooltipOpen, setTooltipOpen] = useState<boolean>(false);

    const handleToolTipToggleMove = (val: boolean) => {
      setTooltipOpen && setTooltipOpen(val);
    }

    const titleImages = getProjectImages(team, i18n.language);

    useEffect(() => {
      if (!team) return;
      //@ts-ignore
      setIsBookmarked(team.isBookmark);
    }, [team]);
  
    const handleTeamClick = (projectId?: string, e?: any) => {
      const openInMedia = [/*ModerationType.Channel,*/ ModerationType.ChannelGroupOrCourse, ModerationType.Outer]?.includes(team?.moderationType ?? 0)
      team && onTeamClick && onTeamClick(team, e, openInMedia);
    }

    const lmsCardType = useMemo(() => {
      if (isPersonalOrDefaultChannel(team, profile) && entityType === DesignerTab.educationSubscribes) {
        return 'fixed-ratio-small'
      }
      return undefined;
    }, [team, entityType]);

    const horizontalCover = useMemo(() => {
      //@ts-ignore
      return (team?.lmsCourseTerm === LMSCourseDurationTypes.short) /*|| (isUnitCourse && team?.lmsCourseTerm === null)*/
    } , [team])
    // @ts-ignore
    const teamOwner = useMemo(() => team?.owners?.length > 0 ? `${team?.owners[0]?.name} ${team?.owners[0]?.surName}` : '', [team])
    
    const isArchiveCourseChannel = useMemo(() => {
      return team?.moderationType === ModerationType.ChannelGroupOrCourse && TeamStatusActiveArchived(team)
    } , [team])

    const backgroundStyles = useMemo(() => {
      if (isArchiveCourseChannel) {
        return {
          background: 'linear-gradient( 180deg,rgba(0, 0, 0, 0),  rgba(24, 26, 28, 0), rgba(24, 26, 28, 0.85) )',
          backdropFilter: 'blur(2.5px)',
          marginTop: 0
        }
      }
      return undefined
    } , [isArchiveCourseChannel])


    const badge = useMemo(() => {
      if (isArchiveCourseChannel) {
        return <StatusText hasCover={!!titleImages?.length}>{t('subscription.in_archive').toLowerCase()}</StatusText>
      }
      return undefined
    } , [isArchiveCourseChannel, titleImages])

    const title = useMemo(() => {
      if (isTimeLine) return null;

      else if (team?.moderationType === ModerationType.ChannelGroupOrCourse) {
        return <ProjectCardTitle {...(isArchiveCourseChannel ? { color: '#909090'} : {})}>{getChannelName(team as unknown as Team, i18n.language)}</ProjectCardTitle>
      }
      else if (team?.coverTitle?.length) {
        return <ProjectCardTitle >{team?.coverTitle}</ProjectCardTitle>
      } 
      else if (team?.displayName?.length) {
        return <ProjectCardTitle {...(isArchiveCourseChannel ? { color: '#909090'} : {})}>{team?.displayName}</ProjectCardTitle>
      }
      else if (team?.name?.length) {
        return <ProjectCardTitle {...(isArchiveCourseChannel ? { color: '#909090'} : {})}>{team?.name}</ProjectCardTitle>
      } else return null;
    }, [team, isTimeLine, isArchiveCourseChannel])

    const subscribersCount = useMemo(() => team?.subscribersCount , [team])

    const likes = useMemo(() => {
      const color = isMedia && isMobile ? Colors.galBlack100 : Colors.geoWhite100
      if (!subscribersCount || subscribersCount === 0) return null;
      return <Author style={{ color }}>{t('team_view.subscribers', { count: subscribersCount })}</Author>
    } , [team, subscribersCount])

      return (
      <ProjectCardLayoutNew
        title={title}
        coverTitle={!!team?.name ? <ProjectCardTitle>{team?.name}</ProjectCardTitle> : undefined}
        identifier={team?.teamId}
        onClick={handleTeamClick}
        titleImages={titleImages}
        isLoading={isLoading}
        width={width}
        team={team}
        height={height}
        isMoreVisible={true}
        isRejected={isRejected}
        metaAlwaysVisible={isTab && isEditable}
        tooltipOpen={isTooltipOpen}
        toggleTooltip={handleToolTipToggleMove}
        needLmsCover={horizontalCover}
        badge={badge}
        isMobileMoreVisible={isArchiveCourseChannel || isEditable}
        backgroundStyles={backgroundStyles}
        linkTo={linkTo}
        more={isEditable ? (
          <ProjectCardTools 
            isEditable
            tooltipOpen={isTooltipOpen}
            toggleTooltip={handleToolTipToggleMove}
            onEdit={() => {
              const isOuterTeam = team?.moderationType === ModerationType.Outer
              window.open(window.location.origin + `/${isOuterTeam ? 'ext-' : ''}team-edit/` + team?.teamId, '_SELF')
            }}
          />
        ) : null}
        
        // @ts-ignore
        projectType={lmsCardType}
        lmsCardType={lmsCardType}
        withoutGoToProject={!linkTo}
        author={
          (
            <Author>
              {/*@ts-ignore*/}
              <span style={{ color: isMedia && isMobile ? '#151514' :'#fefefe'}}>{teamOwner}</span>
            </Author>
          )
        }
        likes={likes}
        isTeam
      />
    );
  }
;

export default memo(ChannelCard);
